import { useCallback } from "react";
import { useBoard } from "../../../hooks";
import { useConnectionViewModel } from "./Connection.ViewModel";
import { useConnectionsLevelViewModel } from "./ConnectionsLevel.ViewModel";
import type {
  ConnectionsViewModel,
  ConnectionsViewModelProps,
} from "./Connections.ViewModel.types";

export const useConnectionsViewModel = ({
  boardId,
  hubConnectionId,
  boardsApi,
  sourcesApi,
  loadErrorMessage,
  saveErrorMessage,
  deleteErrorMessage,
  invalidConfigurationMessage,
  confirmDeleteConnection,
  launchAuthorisation,
  voteStorage,
  feedbackApi,
  showBacklog = true,
  additionalPlannedSources,
}: ConnectionsViewModelProps): ConnectionsViewModel => {
  const boardQuery = useBoard(boardsApi, boardId || "", { enabled: !!boardId });
  const connectionsLevel = useConnectionsLevelViewModel({
    boardId,
    hubConnectionId,
    boardsApi,
    sourcesApi,
    loadErrorMessage,
    saveErrorMessage,
    deleteErrorMessage,
    voteStorage,
    feedbackApi,
    showBacklog,
    additionalPlannedSources,
  });

  const connectionLevel = useConnectionViewModel({
    boardId,
    boardsApi,
    allSources: connectionsLevel.allSources,
    saveConnection: connectionsLevel.saveConnection,
    deleteConnection: connectionsLevel.deleteConnection,
    reload: connectionsLevel.reload,
    loadErrorMessage,
    saveErrorMessage,
    invalidConfigurationMessage,
    confirmDeleteConnection,
    launchAuthorisation,
    setActionErrorMessage: connectionsLevel.setActionErrorMessage,
    setIsAuthorisingConnection: connectionsLevel.setIsAuthorisingConnection,
  });

  const connectSource = useCallback(
    async (sourceId: string): Promise<void> => {
      const source = connectionsLevel.getSourceById(sourceId);
      if (!source || !boardId) {
        return;
      }

      connectionsLevel.setActionErrorMessage("");
      connectionsLevel.setBillingRefusal(null);

      if (source.oneClickCreateUrl) {
        if (!launchAuthorisation) {
          return;
        }

        connectionsLevel.setIsAuthorisingConnection(true);
        try {
          const shouldReload = await launchAuthorisation({
            mode: "connect",
            url: source.oneClickCreateUrl
              .replace("{boardId}", boardId)
              .replace("{sourceId}", source.id),
            source,
          });

          if (shouldReload) {
            await connectionsLevel.reload();
          }
        } finally {
          connectionsLevel.setIsAuthorisingConnection(false);
        }
        return;
      }

      const created = await connectionsLevel.createConnection(source.id);
      if (created && source.goToSettingsOnCreate) {
        connectionLevel.openConnectionSettings(created);
      }
    },
    [boardId, connectionLevel, connectionsLevel, launchAuthorisation],
  );

  const hasError = boardQuery.isError || connectionsLevel.sourcesFailed;

  return {
    isLoading: boardQuery.isLoading || connectionsLevel.isLoadingSources,
    isCreatingConnection: connectionsLevel.isCreatingConnection,
    isSavingConnection: connectionsLevel.isSavingConnection,
    isDeletingConnection: connectionsLevel.isDeletingConnection,
    isAuthorisingConnection: connectionsLevel.isAuthorisingConnection,
    hasError,
    errorMessage: hasError ? loadErrorMessage : "",
    actionErrorMessage: connectionsLevel.actionErrorMessage,
    billingRefusal: connectionsLevel.billingRefusal,
    clearBillingRefusal: () => connectionsLevel.setBillingRefusal(null),
    boardName: boardQuery.data?.name || "",
    statuses: boardQuery.data?.statuses || [],
    allSources: connectionsLevel.allSources,
    connections: connectionsLevel.connections,
    filteredSources: connectionsLevel.filteredSources,
    connectedPrivacySources: connectionsLevel.connectedPrivacySources,
    searchText: connectionsLevel.searchText,
    editingConnection: connectionLevel.editingConnection,
    canReAuthoriseEditingConnection:
      connectionLevel.canReAuthoriseEditingConnection,
    isDirty: connectionLevel.isDirty,
    votedSourceIds: connectionsLevel.votedSourceIds,
    setSearchText: connectionsLevel.setSearchText,
    voteForSource: connectionsLevel.voteForSource,
    hasVotedForSource: connectionsLevel.hasVotedForSource,
    connectSource,
    createConnection: connectionsLevel.createConnection,
    saveConnection: connectionsLevel.saveConnection,
    deleteConnection: connectionsLevel.deleteConnection,
    openConnectionSettings: connectionLevel.openConnectionSettings,
    closeConnectionSettings: connectionLevel.closeConnectionSettings,
    updateEditingConnection: connectionLevel.updateEditingConnection,
    saveEditingConnection: connectionLevel.saveEditingConnection,
    deleteEditingConnection: connectionLevel.deleteEditingConnection,
    reAuthoriseEditingConnection: connectionLevel.reAuthoriseEditingConnection,
    canReAuthoriseConnection: connectionLevel.canReAuthoriseConnection,
    reAuthoriseConnection: connectionLevel.reAuthoriseConnection,
    clearActionError: () => connectionsLevel.setActionErrorMessage(""),
    reload: connectionsLevel.reload,
  };
};
